import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import BulkImportDialog from "@/components/BulkImportDialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Built-in pipelines and their stages (mirrors the pipeline configs used across
// the app). Custom pipelines are appended from /admin/pipelines.
const BUILTIN_PIPELINES = [
  {
    id: "mbbs",
    name: "MBBS",
    stages: ["New", "Contacted", "Follow-up", "Zoom Done", "Visited", "Docs Recd", "Enrolled", "Closed-lost", "Wrong number"],
  },
  {
    id: "general",
    name: "Leads",
    stages: ["Untouched", "Not Connected", "Cold", "Warm", "Future Lead", "Closed", "Referred"],
  },
  {
    id: "counseling",
    name: "Counselling",
    stages: ["New", "Docs Received", "Visited", "Zoom Done", "Options Sent", "Enrolled", "Spoke", "Future Lead", "Closed"],
  },
];

export default function DataSettingsTab() {
  const [bulkOpen, setBulkOpen] = useState(false);
  const [customPipelines, setCustomPipelines] = useState([]);
  const [pipeline, setPipeline] = useState("all");
  const [stage, setStage] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/pipelines");
        setCustomPipelines(
          (data?.pipelines || []).map((p) => ({
            id: p.id,
            name: p.name,
            stages: (p.stages || []).map((s) => s.label),
          })),
        );
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  const pipelines = useMemo(
    () => [...BUILTIN_PIPELINES, ...customPipelines],
    [customPipelines],
  );

  const stages = useMemo(() => {
    if (pipeline === "all") return [];
    return pipelines.find((p) => p.id === pipeline)?.stages || [];
  }, [pipeline, pipelines]);

  const onPipelineChange = (v) => {
    setPipeline(v);
    setStage("all"); // reset stage whenever pipeline changes
  };

  // Resolve the selected preset (or custom inputs) into inclusive ISO day bounds.
  const resolveDateRange = () => {
    const pad = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    let from = "";
    let to = "";
    const now = new Date();
    if (datePreset === "today") {
      from = pad(now);
    } else if (datePreset === "7d") {
      const d = new Date(now);
      d.setDate(d.getDate() - 6);
      from = pad(d);
    } else if (datePreset === "30d") {
      const d = new Date(now);
      d.setDate(d.getDate() - 29);
      from = pad(d);
    } else if (datePreset === "month") {
      from = pad(new Date(now.getFullYear(), now.getMonth(), 1));
    } else if (datePreset === "custom") {
      from = dateFrom;
      to = dateTo;
    }
    return {
      date_from: from ? `${from}T00:00:00` : "",
      date_to: to ? `${to}T23:59:59` : "",
    };
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("medix_admin_token");
      const params = new URLSearchParams();
      if (pipeline !== "all") params.set("pipeline", pipeline);
      if (stage !== "all") params.set("status_filter", stage);
      const { date_from, date_to } = resolveDateRange();
      if (date_from) params.set("date_from", date_from);
      if (date_to) params.set("date_to", date_to);
      const res = await fetch(
        `${API}/admin/leads/export.csv${params.toString() ? `?${params}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const tag = pipeline === "all" ? "all" : pipeline;
      link.download = `medadmits-leads-${tag}-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error(formatApiError(e) || "CSV export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="data-settings-tab">
      {/* Export CSV */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-[#0A2540]" />
          <h3 className="text-sm font-semibold text-[#0A2540]">Export leads to CSV</h3>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Download your leads as a spreadsheet. Filter by pipeline and stage to export a specific segment.
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Pipeline</Label>
            <Select value={pipeline} onValueChange={onPipelineChange}>
              <SelectTrigger className="mt-1.5" data-testid="export-pipeline-select">
                <SelectValue placeholder="All pipelines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All pipelines</SelectItem>
                {pipelines.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`export-pipeline-${p.id}`}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Stage</Label>
            <Select value={stage} onValueChange={setStage} disabled={pipeline === "all"}>
              <SelectTrigger className="mt-1.5" data-testid="export-stage-select">
                <SelectValue placeholder="All stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {stages.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`export-stage-${s.replace(/\W+/g, "-").toLowerCase()}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {pipeline === "all" && (
              <p className="mt-1 text-[10px] text-slate-400">Select a pipeline to filter by stage.</p>
            )}
          </div>
        </div>

        {/* Date range */}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Date range (created)</Label>
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="mt-1.5" data-testid="export-date-preset-select">
                <SelectValue placeholder="All time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="month">This month</SelectItem>
                <SelectItem value="custom">Custom range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {datePreset === "custom" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] text-slate-500">From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  max={dateTo || undefined}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-1.5"
                  data-testid="export-date-from"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] text-slate-500">To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-1.5"
                  data-testid="export-date-to"
                />
              </div>
            </div>
          )}
        </div>

        <Button
          className="mt-4 gap-2 bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
          onClick={exportCsv}
          disabled={exporting}
          data-testid="export-csv-btn"
        >
          <Download className="h-4 w-4" />
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </div>

      {/* Bulk Import */}
      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <Upload className="h-5 w-5 text-[#0A2540]" />
          <h3 className="text-sm font-semibold text-[#0A2540]">Bulk import leads</h3>
        </div>
        <p className="mt-1 text-xs text-slate-500">
          Upload a CSV to add many leads at once. Download the sample file inside the importer to see the expected columns.
        </p>
        <Button
          variant="outline"
          className="mt-4 gap-2"
          onClick={() => setBulkOpen(true)}
          data-testid="open-bulk-import-btn"
        >
          <Upload className="h-4 w-4" /> Bulk Import
        </Button>
      </div>

      <BulkImportDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onImported={() => toast.success("Leads imported")}
      />
    </div>
  );
}
